import React, {useContext} from 'react'
import propTypes from 'prop-types'
import { withStyles } from '@material-ui/core'
import MuiSlider from '@material-ui/core/Slider'
import Box from '@material-ui/core/Box'
import { NumberField } from '../Form/Fields'
import toFinite from 'lodash/toFinite'
import get from 'lodash/get'

import { R } from '../../utils/currency'
import { ApplicationContext } from '../../contexts/ApplicationContext'

const ThemedSlider = withStyles({
  root: {
    color: ({ theme }) => theme.secondaryOneColor,
    height: '0.625rem',
  },
  thumb: {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px 0 #a89d9d',
    marginTop: -8,
    marginLeft: -12,
    '&:focus, &:hover, &$active': {
      boxShadow: '0 2px 4px 0 #a89d9d',
    },
  },
  active: {},
  valueLabel: {
    left: 'calc(-50% + 4px)',
  },
  track: {
    height: '0.625rem',
    borderRadius: 3,
  },
  rail: {
    height: '0.625rem',
    borderRadius: 3,
  },
})(MuiSlider)

/* Base themed slider component
 */
export const Slider = (props) => {
  const { theme } = useContext(ApplicationContext)
  return <ThemedSlider theme={theme} {...props} />
}

/*
 * Slider component that contains a title, min-max labels, and the
 * current value of the slider as it is used.
 * @param rest - contains the props that are required for the MuiSlider
 *  and optionally contains a Formik Field prop.
 *  If the slider is intended to be used as an input for a form,
 *  it MUST be used with a Formik Field.
 *  Example:
 *    <Field
 *      {...MuiSliderProps}
 *      name={name}
 *      component={SliderWithLabels}
 *    />
 *
 * This component is usually wrapped by the
 * components/Form/Fields -> SliderFieldWithLabels component.
 * In cases where it is not used as a form input - this SliderWithLabels
 * component is used directly.
 */
export const SliderWithLabels = ({ title, format, mappingValues, formatCallback, ...rest }) => {
  const { field } = rest
  const name = get(field, 'name')
  const value = get(field, 'value')

  const defaultValue = formatValue(format, rest.defaultValue, mappingValues, formatCallback)
  const min = formatValue(format, rest.min, mappingValues, formatCallback)
  const max = formatValue(format, rest.max, mappingValues, formatCallback)
  const currentValue = formatValue(format, value, mappingValues, formatCallback)

  return (
    <Box className='app-slider' width='100%'>
      <h5>{title}</h5>
      <label style={{ textAlign: 'left' }}>{defaultValue}</label>
      <Box display='flex' justifyContent='space-between'>
        <p>{min}</p>
        <p>{max}</p>
      </Box>
      <Slider
        name={name}
        value={value}
        {...rest}
      />
      <Box display='flex' justifyContent='center'><label>{currentValue}</label></Box>
    </Box>
  )
}

export const SliderInlineWithLabels = ({ title, format, withoutError, disabled, mappingValues, formatCallback, ...rest }) => {
  const { field } = rest
  const name = get(field, 'name')
  const value = get(field, 'value')

  const defaultValue = formatValue(format, rest.defaultValue, mappingValues, formatCallback)
  const min = formatValue(format, rest.min, mappingValues, formatCallback)
  const max = formatValue(format, rest.max, mappingValues, formatCallback)
  const currentValue = formatValue(format, value, mappingValues, formatCallback)

  const baseClass = 'inline-slider'
  return (
    <Box className='app-slider inline-slider'>
      <label style={{ textAlign: 'left' }}>{title}</label>
      <Box display='flex'>
        <Box className='slider-controls'>
          <Box display='flex' justifyContent='space-between'>
            <p>{min}</p>
            <p>{max}</p>
          </Box>
          <Slider
            name={name}
            value={value}
            {...rest}
          />
        </Box>
        <NumberField name={name} disabled={disabled} className="slider-input-field" />
      </Box>
    </Box>
  )
}

const formatValue = (format, value, mappingValues, formatCallback) => {
  switch (format) {
    case 'currency':
      return R(value).format()
    case 'mappingValues':
      return mappingValues[toFinite(value)]
    case 'formatCallback':
      return formatCallback(value)
    default:
      return toFinite(value)
  }
}

Slider.propTypes = {
  defaultValue: propTypes.number.isRequired,
  name: propTypes.string,
  value: propTypes.number,
  min: propTypes.number,
  max: propTypes.number,
  step: propTypes.number,
  onChange: propTypes.func,
}

export const SliderFieldWithLabelsPropTypes = {
  defaultValue: propTypes.number.isRequired,
  title: propTypes.string.isRequired,
  name: propTypes.string,
  min: propTypes.number,
  max: propTypes.number,
  step: propTypes.number,
  /*
   * The labels of the slider field can be formatted using the following options:
   *  - currency, the value will be treated and formatted as a currency
   *    e.g.
   *      R 20 000
   *  - mappingValues, the value will be used as an index to a supplied array to
   *    map the value to the element in array
   *    e.g.
   *      value = 2
   *      mappingValues = ['', 'foo', 'bar']
   *      then the formatted value will be 'bar'
   *  - formatCallback, the value will be passed to a supplied formatCallback function
   *    which must return the formatted value
   */
  format: propTypes.oneOf(['currency', 'mappingValues', 'formatCallback']),
  mappingValues: (props) => {
    if (props.format === 'mappingValues' && !props.mappingValues) {
      return new Error('You need to supply an a array of values to map if format  === mappingValues')
    }
  },
  formatCallback: (props) => {
    if (props.format === 'formatCallback' && (!props.formatCallback || typeof(props.formatCallback) !== 'function')) {
      return new Error('You need to supply and formatCallback function if format === formatCallback')
    }
  }
}

SliderWithLabels.propTypes = SliderFieldWithLabelsPropTypes
